import { html, nothing } from "lit";
import { state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";

import "@phosphor-icons/webcomponents/PhGlobeSimple";
import "@phosphor-icons/webcomponents/PhPlus";
import "ui-components/button/wow-button";
import "ui-components/tab-bar/tab-bar";
import type { TabItem } from "ui-components/tab-bar/tab-bar";
import "ui-components/form-elements/input/wow-input";
import "ui-components/form-elements/textarea/textarea";
import "ui-components/form-elements/select/wow-select";
import "ui-components/form-elements/switch/switch";
import "ui-components/form-elements/autocomplete-multi-select/autocomplete-multi-select";
import "ui-components/media-uploader/media-uploader";
import "ui-components/centered-dialog/wow-centered-dialog";
import "../../components/link-card";
import "../../components/create-link-modal/new-create-link-modal";
import "../../components/co-promotion-toggle/co-promotion-toggle";

import { styles } from "./community-form-page.css";
import { SignalElement, inject } from "ui-common/di";
import { LayoutController } from "ui-common/layout-controller";
import type { IAuthState } from "ui-common/services/auth/types";
import type {
  ICommunityFormState,
  ICommunityFormService,
  CommunityFormData,
  CommunityUIToggles,
} from "../../services/community-form/types";
import type { ICreatorCommunitiesService } from "../../services/creator-communities/types";
import type { IBackofficeFeatureFlagsState } from "../../services/backoffice-feature-flags/types";
import type { IBackofficeLinksState, IBackofficeLinksService } from "../../services/backoffice-links/types";
import type { UnifiedLink } from "api-contracts/rpc/get-unified-links";
import type { DraggedItem } from "../../controllers/dnd-container-controller";
import { DnDContainerController } from "../../controllers/dnd-container-controller";
import { DnDDropIndicatorController } from "../../controllers/dnd-drop-indicator-controller";
import { navigateTo } from "../../router";
import type { RoutePath } from "../../router";
import { NotificationService } from "../../../../../libs/ui-components/notification";
import { t, translate } from "../../translations/i18n";

// Route will be registered in Phase 5 wiring
const COMMUNITIES_ROUTE = "communities" as RoutePath;

type CommunityTab = "info" | "links";
type LinkFilter = "live" | "upcoming" | "expired";

interface LinkItemWithId extends DraggedItem {
  id: string;
  title: string;
  url: string;
  imageUrl: string | null;
}

export abstract class CommunityFormPage extends SignalElement {
  static override styles = [styles];

  @inject<IAuthState>("authState")
  protected authState!: IAuthState;

  @inject<ICommunityFormState>("communityFormState")
  protected formState!: ICommunityFormState;

  @inject<ICommunityFormService>("communityFormService")
  protected formService!: ICommunityFormService;

  @inject<ICreatorCommunitiesService>("creatorCommunitiesService")
  private communitiesService!: ICreatorCommunitiesService;

  @inject<IBackofficeFeatureFlagsState>("backofficeFeatureFlagsState")
  private featureFlags!: IBackofficeFeatureFlagsState;

  @inject<IBackofficeLinksState>("backofficeLinksState")
  private linksState!: IBackofficeLinksState;

  @inject<IBackofficeLinksService>("backofficeLinksService")
  private linksService!: IBackofficeLinksService;

  protected layoutController = new LayoutController(this);

  private linksContainerRef = createRef<HTMLElement>();

  private dndController = new DnDContainerController<LinkItemWithId>(this, this.linksContainerRef, {
    onReorder: async (newOrder: LinkItemWithId[]) => {
      const linkIds = newOrder.map((link) => link.id);
      try {
        await this.linksService.reorderCommunityLinks(linkIds);
      } catch (error) {
        console.error("Failed to reorder community links:", error);
        NotificationService.error(translate("reorderLinks.reorderError"));
      }
    },
    onDropComplete: (droppedItem: LinkItemWithId) => {
      this.flourishCardId = droppedItem.id;
      setTimeout(() => {
        this.flourishCardId = null;
      }, 600);
    },
    getValidDropPositions: (sourceItem: LinkItemWithId) => {
      return sourceItem.imageUrl ? ["left", "right"] : ["top", "bottom"];
    },
  });

  private dropIndicatorController = new DnDDropIndicatorController(this, this.linksContainerRef);

  @state() protected activeTab: CommunityTab = "info";
  @state() private showCancelConfirmation = false;
  @state() private showCreateLinkModal = false;
  @state() private selectedLinkFilter: LinkFilter = "live";
  @state() private flourishCardId: string | null = null;

  private cancelConfirmResolver?: (result: boolean) => void;

  protected isSaved = false;

  abstract get mode(): "create" | "edit";
  abstract get pageTitle(): string;

  protected onAutoSaveDraft(): Promise<void> {
    return Promise.resolve();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.formService.reset();
  }

  // --- Tab switching ---
  private handleTabChange = async (e: CustomEvent<{ key: string }>) => {
    const tab = e.detail.key as CommunityTab;
    if (tab === "links") {
      if (!this.formState.communityId.get()) {
        await this.formService.saveAsDraft();
        await this.onAutoSaveDraft();
      }
      const communityId = this.formState.communityId.get();
      if (communityId) {
        this.linksService.loadCommunityLinks(communityId);
      }
    }
    this.activeTab = tab;
  };

  // --- Form field handlers ---
  private handleFieldInput<K extends keyof CommunityFormData>(field: K, e: Event): void {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    this.formService.updateFormData(field, target.value as CommunityFormData[K]);
  }

  private handleLanguageChange(e: CustomEvent<{ value: string }>): void {
    this.formService.updateFormData("language", e.detail.value);
  }

  private handleToggle<K extends keyof CommunityUIToggles>(toggle: K, e: CustomEvent<{ checked: boolean }>): void {
    this.formService.updateUIToggle(toggle, e.detail.checked as CommunityUIToggles[K]);
  }

  private handleLinkedEventsChange(e: CustomEvent<{ value: string[] }>): void {
    const currentLinked = this.formState.linkedEventIds.get();
    const newLinked = e.detail.value;

    for (const id of currentLinked) {
      if (!newLinked.includes(id)) {
        this.formService.toggleLinkedEvent(id, false);
      }
    }
    for (const id of newLinked) {
      if (!currentLinked.includes(id)) {
        this.formService.toggleLinkedEvent(id, true);
      }
    }
  }

  private handleThumbnailUpload(e: CustomEvent<{ fileId: string; imageUrl: string }>): void {
    this.formService.setThumbnailData(e.detail.fileId, e.detail.imageUrl);
  }

  // --- Cancel / Save / Publish ---
  private handleCancel(): void {
    this.showCancelConfirmation = true;
  }

  async canDeactivate(): Promise<boolean> {
    if (this.isSaved) return true;
    return new Promise((resolve) => {
      this.cancelConfirmResolver = resolve;
      this.showCancelConfirmation = true;
    });
  }

  private handleCancelConfirmStay(): void {
    this.showCancelConfirmation = false;
    if (this.cancelConfirmResolver) {
      this.cancelConfirmResolver(false);
      this.cancelConfirmResolver = undefined;
    }
  }

  private handleCancelConfirmLeave(): void {
    this.showCancelConfirmation = false;
    if (this.cancelConfirmResolver) {
      this.cancelConfirmResolver(true);
      this.cancelConfirmResolver = undefined;
    } else {
      navigateTo(COMMUNITIES_ROUTE);
    }
  }

  private async handleSaveAsDraft(): Promise<void> {
    const success = await this.formService.saveAsDraft();
    if (!success) return;

    this.isSaved = true;
    this.communitiesService.refetch();
    NotificationService.success(translate("communitiesPage.formSaveAsDraftSuccess"));
    this.showCancelConfirmation = false;
    if (this.cancelConfirmResolver) {
      this.cancelConfirmResolver(true);
      this.cancelConfirmResolver = undefined;
    } else {
      navigateTo(COMMUNITIES_ROUTE);
    }
  }

  private async handlePublish(): Promise<void> {
    if (!this.formService.validateForm()) {
      NotificationService.error(translate("communitiesPage.formValidationError"));
      return;
    }

    const success = await this.formService.publish();
    if (success) {
      this.isSaved = true;
      this.communitiesService.refetch();
      NotificationService.success(translate("communitiesPage.formPublishSuccess"));
      navigateTo(COMMUNITIES_ROUTE);
    }
  }

  // --- Tab configuration ---
  private get formTabs(): TabItem[] {
    return [
      { key: "info", label: translate("communitiesPage.tabInfo"), testId: "community-info-tab" },
      { key: "links", label: translate("communitiesPage.tabLinksDiscounts"), testId: "community-links-tab" },
    ];
  }

  // --- Render: Sidebar (left column / info tab) ---
  private renderSidebarInfoContent() {
    const formData = this.formState.formData.get();
    const uiToggles = this.formState.uiToggles.get();
    const linkedEventIds = this.formState.linkedEventIds.get();
    const availableEvents = this.formState.availableEvents.get();

    return html`
      <div class="form-section">
        <wow-select
          label=${translate("communitiesPage.formLanguageLabel")}
          name="language"
          .options=${[
            { value: "nl", label: translate("communitiesPage.languageNederlands") },
            { value: "en", label: translate("communitiesPage.languageEnglish") },
          ]}
          .value=${formData.language}
          @change=${(e: CustomEvent) => this.handleLanguageChange(e)}
        >
          <ph-globe-simple slot="prefix" size="20"></ph-globe-simple>
        </wow-select>
      </div>

      <hr class="section-divider" />

      <div class="toggle-field">
        <wow-switch
          label=${translate("communitiesPage.formLinkEventsToggle")}
          ?checked=${uiToggles.showLinkedEvents}
          @change=${(e: CustomEvent) => this.handleToggle("showLinkedEvents", e)}
        ></wow-switch>
        ${uiToggles.showLinkedEvents
          ? html`
              <div class="toggle-sub-fields">
                <wow-autocomplete-multi-select
                  placeholder=${translate("communitiesPage.formEventsSearchPlaceholder")}
                  no-results-text=${translate("communitiesPage.formEventsNotFound")}
                  loading-text=${translate("communitiesPage.formEventsLoading")}
                  .options=${availableEvents}
                  .selectedValues=${linkedEventIds}
                  @change=${(e: CustomEvent) => this.handleLinkedEventsChange(e)}
                ></wow-autocomplete-multi-select>
              </div>
            `
          : nothing}
      </div>

      <hr class="section-divider" />

      <div class="toggle-field">
        <wow-switch
          label=${translate("communitiesPage.formCustomCtaToggle")}
          ?checked=${uiToggles.showCustomCta}
          @change=${(e: CustomEvent) => this.handleToggle("showCustomCta", e)}
        ></wow-switch>
        ${uiToggles.showCustomCta
          ? html`
              <div class="toggle-sub-fields">
                <wow-input
                  label=${translate("communitiesPage.formCtaTextLabel")}
                  placeholder=${translate("communitiesPage.formCtaTextPlaceholder")}
                  maxlength="50"
                  .value=${formData.ctaText}
                  @input=${(e: Event) => this.handleFieldInput("ctaText", e)}
                ></wow-input>
              </div>
            `
          : nothing}
      </div>

      ${this.renderCoPromotionToggle()}

      <hr class="section-divider" />

      <div class="toggle-field">
        <wow-switch
          label=${translate("communitiesPage.formShowMemberCountToggle")}
          ?checked=${formData.showMemberCount}
          @change=${(e: CustomEvent) =>
            this.formService.updateFormData("showMemberCount", (e.target as HTMLInputElement).checked)}
        ></wow-switch>
      </div>
    `;
  }

  private handleCollaboratorsSaved(e: CustomEvent) {
    this.formService.setCollaboratorHandles(e.detail.collaborators);
  }

  private renderCoPromotionToggle() {
    if (!this.featureFlags.hasFeature("co-promotions")) return nothing;

    const uiToggles = this.formState.uiToggles.get();

    return html`
      <co-promotion-toggle
        ?checked=${uiToggles.showCoPromotion}
        .collaboratorHandles=${this.formState.collaboratorHandles.get()}
        .existingCollaborators=${this.formState.existingCollaborators.get()}
        @toggle-change=${(e: CustomEvent) => this.handleToggle("showCoPromotion", e)}
        @collaborators-saved=${this.handleCollaboratorsSaved}
      ></co-promotion-toggle>
    `;
  }

  // --- Links tab ---
  private getFilteredLinks(links: UnifiedLink[], filter: LinkFilter): UnifiedLink[] {
    switch (filter) {
      case "live":
        return links.filter((l) => l.status === "live");
      case "upcoming":
        return links.filter((l) => l.status === "upcoming");
      case "expired":
        return links.filter((l) => l.status === "expired");
      default:
        return links;
    }
  }

  private handleLinkFilterChange = (filter: LinkFilter) => {
    this.selectedLinkFilter = filter;
  };

  private handleOpenCreateLinkModal = () => {
    this.showCreateLinkModal = true;
  };

  private handleCreateLinkModalClose = () => {
    this.showCreateLinkModal = false;
  };

  private handleLinkSaved = () => {
    this.linksService.reloadCommunityLinks();
  };

  private handleDnDDropRequested = () => {
    const linksData = this.linksState.communityLinks.get();
    if (linksData?.links) {
      const filteredLinks = this.getFilteredLinks(linksData.links, this.selectedLinkFilter);
      const links = filteredLinks.map((ld) => ({
        ...ld,
        imageUrl: ld.promotion_file?.path ?? null,
      }));
      this.dndController.processDropWithItems(links);
    }
  };

  private renderSidebarLinksContent() {
    const communityId = this.formState.communityId.get();
    const linksData = this.linksState.communityLinks.get();
    const isLoading = this.linksState.communityLinksLoading.get();
    const links = linksData?.links ?? [];
    const counts = linksData?.counts ?? { live: 0, upcoming: 0, expired: 0, total: 0 };
    const filteredLinks = this.getFilteredLinks(links, this.selectedLinkFilter);

    return html`
      <div class="community-links-content">
        ${links.length
          ? html`
              <div class="community-links-header">
                <div class="community-links-filters">
                  <wow-button
                    size="small"
                    variant=${this.selectedLinkFilter === "live" ? "primary" : "subtle"}
                    @click=${() => this.handleLinkFilterChange("live")}
                    ?disabled=${counts.live === 0}
                  >
                    ${t("links.filters.live")} (${counts.live})
                  </wow-button>
                  <wow-button
                    size="small"
                    variant=${this.selectedLinkFilter === "upcoming" ? "primary" : "subtle"}
                    @click=${() => this.handleLinkFilterChange("upcoming")}
                    ?disabled=${counts.upcoming === 0}
                  >
                    ${t("links.filters.upcoming")} (${counts.upcoming})
                  </wow-button>
                  ${counts.expired
                    ? html`
                        <wow-button
                          size="small"
                          variant=${this.selectedLinkFilter === "expired" ? "primary" : "subtle"}
                          @click=${() => this.handleLinkFilterChange("expired")}
                        >
                          ${t("links.filters.expired")} (${counts.expired})
                        </wow-button>
                      `
                    : nothing}
                </div>
                <wow-button variant="primary" size="small" @click=${this.handleOpenCreateLinkModal}>
                  <ph-plus slot="prefix" size="16"></ph-plus>
                  ${t("communitiesPage.addLink")}
                </wow-button>
              </div>
            `
          : nothing}
        ${isLoading
          ? html`<div class="links-loading">${t("communitiesPage.formEventsLoading")}</div>`
          : links.length === 0
            ? html`
                <div class="links-empty-state">
                  <p>${t("communitiesPage.linksEmptyText")}</p>
                  <wow-button variant="primary" size="small" @click=${this.handleOpenCreateLinkModal}>
                    <ph-plus slot="prefix" size="16"></ph-plus>
                    ${t("communitiesPage.addLink")}
                  </wow-button>
                </div>
              `
            : html`
                <div
                  ${ref(this.linksContainerRef)}
                  class="community-links-grid ${this.dndController.isDragging ? "drag-active" : ""}"
                  style="position: relative;"
                  @dnd-drop-requested=${this.handleDnDDropRequested}
                >
                  ${repeat(
                    filteredLinks,
                    (link) => link.id,
                    (link) =>
                      html`<link-card
                        .link=${link}
                        .flourish=${this.flourishCardId === link.id}
                        @link-deleted=${this.handleLinkSaved}
                        @link-created=${this.handleLinkSaved}
                        @link-edited=${this.handleLinkSaved}
                      ></link-card>`,
                  )}
                  ${this.dropIndicatorController.renderDropIndicator(
                    this.dndController.currentDropPosition,
                    this.dndController.currentDropPosition ? this.dndController.getValidDropPositions() : [],
                    this.dndController.isDragging,
                  )}
                </div>
              `}
      </div>

      ${this.showCreateLinkModal && communityId
        ? html`<new-create-link-modal
            communityId=${communityId}
            @modal-close=${this.handleCreateLinkModalClose}
            @link-created=${this.handleLinkSaved}
          ></new-create-link-modal>`
        : nothing}
    `;
  }

  // --- Render: Content (right column) ---
  private renderContentPanel() {
    const formData = this.formState.formData.get();
    const errors = this.formState.validationErrors.get();
    const thumbnailUrl = this.formState.thumbnailUrl.get();
    const isLoading = this.formState.isLoading.get();

    if (isLoading) {
      return html`<div class="loading">${t("communitiesPage.formLoading")}</div>`;
    }

    return html`
      <div class="thumbnail-upload-container">
        <media-uploader
          context="cover_image"
          allowedMediaType="image"
          label=${translate("communitiesPage.formThumbnailLabel")}
          viewport="cover"
          currentFileUrl=${thumbnailUrl ?? ""}
          @change=${(e: CustomEvent) => this.handleThumbnailUpload(e)}
        ></media-uploader>
      </div>
      ${errors.thumbnail ? html`<span class="thumbnail-error">${errors.thumbnail}</span>` : nothing}

      <div>
        <wow-input
          label=${translate("communitiesPage.formCommunityNameLabel")}
          placeholder=${translate("communitiesPage.formCommunityNamePlaceholder")}
          maxlength="50"
          .value=${formData.title}
          error-message=${errors.title ?? ""}
          @input=${(e: Event) => this.handleFieldInput("title", e)}
        ></wow-input>
        <div class="char-count">${formData.title.length} / 50</div>
      </div>

      <div>
        <wow-textarea
          label=${translate("communitiesPage.formDescriptionLabel")}
          placeholder=${translate("communitiesPage.formDescriptionPlaceholder")}
          maxlength="250"
          .value=${formData.description}
          error-message=${errors.description ?? ""}
          @input=${(e: Event) => this.handleFieldInput("description", e)}
        ></wow-textarea>
        <div class="char-count">${formData.description.length} / 250</div>
      </div>
    `;
  }

  // --- Render: Footer ---
  private renderFooter() {
    const isSubmitting = this.formState.isSubmitting.get();

    return html`
      <div class="page-footer">
        <div class="footer-actions">
          <div class="footer-left">
            <wow-button variant="neutral" size="small" @click=${this.handleCancel}
              >${t("communitiesPage.buttonCancel")}</wow-button
            >
          </div>
          <div class="footer-right">
            <wow-button variant="neutral" size="small" ?disabled=${isSubmitting} @click=${this.handleSaveAsDraft}>
              ${isSubmitting ? t("communitiesPage.buttonSavingDraft") : t("communitiesPage.buttonSaveAsDraft")}
            </wow-button>
            <wow-button variant="primary" size="small" ?disabled=${isSubmitting} @click=${this.handlePublish}>
              ${isSubmitting ? t("communitiesPage.buttonPublishing") : t("communitiesPage.buttonPublish")}
            </wow-button>
          </div>
        </div>
      </div>
    `;
  }

  // --- Render: Cancel confirmation dialog ---
  private renderCancelConfirmDialog() {
    if (!this.showCancelConfirmation) return nothing;

    return html`
      <wow-centered-dialog
        header=${translate("communitiesPage.cancelConfirmHeader")}
        description=${translate("communitiesPage.cancelConfirmMessage")}
        @close=${this.handleCancelConfirmStay}
      >
        ${this.mode === "create"
          ? html`<wow-button variant="primary" size="medium" @click=${this.handleSaveAsDraft}>
              ${t("communitiesPage.buttonSaveAsDraft")}
            </wow-button>`
          : nothing}
        <wow-button variant="neutral" size="medium" @click=${this.handleCancelConfirmStay}>
          ${t("communitiesPage.buttonStayEditing")}
        </wow-button>
        <wow-button variant="neutral" size="medium" @click=${this.handleCancelConfirmLeave}>
          ${t("communitiesPage.buttonDiscardChanges")}
        </wow-button>
      </wow-centered-dialog>
    `;
  }

  // --- Render: Layouts ---
  private renderDesktopLayout() {
    return html`
      <div class="page-layout">
        <div class="page-left-column">
          <div class="page-header">
            <h1 class="page-title">${this.pageTitle}</h1>
            <p class="page-subtitle">${t("communitiesPage.formSubtitle")}</p>
          </div>
          <wow-tab-bar
            .tabs=${this.formTabs}
            active-tab=${this.activeTab}
            @tab-change=${this.handleTabChange}
          ></wow-tab-bar>
          <div class="page-left-scroll">
            <div class="form-panel">
              ${this.activeTab === "info" ? this.renderSidebarInfoContent() : this.renderSidebarLinksContent()}
            </div>
          </div>
        </div>
        <div class="page-right-column">
          <div class="content-panel-header">
            <span class="content-panel-label">${t("communitiesPage.pageTitle")}</span>
            <span class="content-panel-subtitle">${t("communitiesPage.formSubtitle")}</span>
          </div>
          <div class="content-panel">${this.renderContentPanel()}</div>
        </div>
      </div>
      ${this.renderFooter()}
    `;
  }

  private renderMobileLayout() {
    return html`
      <div class="page-container">
        <div class="page-header">
          <h1 class="page-title mobile-title">${this.pageTitle}</h1>
        </div>

        <wow-tab-bar
          .tabs=${this.formTabs}
          active-tab=${this.activeTab}
          @tab-change=${this.handleTabChange}
        ></wow-tab-bar>

        ${this.activeTab === "info"
          ? html`
              <div class="mobile-info-tab">${this.renderContentPanel()}</div>
              <hr class="section-divider" />
              ${this.renderSidebarInfoContent()}
            `
          : this.renderSidebarLinksContent()}
      </div>
      ${this.renderFooter()}
    `;
  }

  override render() {
    const content = this.layoutController.isMobile ? this.renderMobileLayout() : this.renderDesktopLayout();

    return html` ${content} ${this.renderCancelConfirmDialog()} `;
  }
}
